import styled, { css } from 'styled-components';
import { Calendar } from '@solent-university/solent-icons';
import NextImage from 'next/image';
import campaignHeading from 'Common/styles/campaignHeading';

import { LinesOpen } from '../HeroBanner/HeroBanner.styled';

export const StyledSection = styled.section<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        padding-bottom: 1.5rem;
        background: linear-gradient(180deg, #cd1423, #a4101c);
        position: relative;
        overflow: hidden;
        margin-top: -2rem;

        @media ${theme.breakpoints.medium.media} {
            padding-bottom: 0;
        }

        @media ${theme.breakpoints.large.media} {
            padding-bottom: ${$isClearingOpen ? '0' : '1.5rem'};
        }
    `}
`;

export const BackgroundWrapper = styled.div`
    height: 100%;
    width: 100%;
    background-image: url('images/clearing/2025/footer/wave-mask-vector-background-crop-top-repeated-v3.svg');
    background-repeat: repeat-x;
    position: absolute;
    background-size: 100rem 8.5625rem;
    top: -0.1rem;
`;

export const Wrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    padding-top: 10rem;
`;

export const Container = styled.div<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        width: 100%;
        padding: ${$isClearingOpen ? '1rem 1rem 0' : '1rem 2.5rem 4rem'};

        @media ${theme.breakpoints.smallLarge.media} {
            padding: ${$isClearingOpen ? '1rem 2.5rem 0' : '1rem 2.5rem 4rem'};
        }

        @media ${theme.breakpoints.large.media} {
            padding: ${$isClearingOpen ? '1rem 2.5rem 0' : '1rem 2.5rem 5rem'};
        }

        @media only screen and (min-width: 93.75rem) {
            padding-top: 0;
        }
    `}
`;

export const FooterWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const Heading = styled.h3<{ $textDefined: boolean; $isClearingOpen: boolean }>`
    ${({ theme, $textDefined, $isClearingOpen }) => css`
        ${campaignHeading()};
        max-width: 80%;
        text-align: center;
        margin-top: 0.75rem;
        margin-bottom: ${!$textDefined || !$isClearingOpen ? '1.5rem' : 'inherit'};
        font-size: ${$isClearingOpen ? '2rem' : '1.75rem'};
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};

        @media ${theme.breakpoints.large.media} {
            margin-top: 0.75rem;
        }

        @media only screen and (min-width: 93.75rem) {
            margin-top: 0;
        }
    `}
`;

export const PhoneWrapper = styled.div`
    ${({ theme }) => css`
        margin-top: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        flex-direction: column;

        @media ${theme.breakpoints.medium.media} {
            gap: 0.75rem;
            flex-direction: row;
        }
    `}
`;

export const FooterLinesOpen = styled(LinesOpen)`
    ${({ theme }) => css`
        color: ${theme.colors.primaryContrast.hex};
        max-width: none;
    `}
`;

export const LinksList = styled.ul`
    ${({ theme }) => css`
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        justify-content: center;
        list-style: none;
        padding: 0;
        padding-bottom: 2rem;

        @media ${theme.breakpoints.large.media} {
            gap: 1.5rem;
        }
    `}
`;

export const Text = styled.p`
    ${({ theme }) => css`
        text-align: center;
        margin-top: 0.5rem;
        margin-bottom: 0;
        color: ${theme.colors.primaryContrast.hex};

        :first-of-type {
            margin-bottom: 0.5rem;

            @media ${theme.breakpoints.large.media} {
                margin-bottom: 1rem;
            }
        }
    `}
`;

export const PostFooter = styled.div<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        width: 100%;
        max-width: 58rem;
        background: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        padding: 1rem;
        color: ${theme.colors.campaigns.clearing.primaryTintEighty.hex};
        margin: ${$isClearingOpen ? '0' : '0 auto'};
        border-radius: 0.5rem;
        gap: 1rem;
        column-gap: 2rem;

        ${Text} {
            margin-bottom: 0;
            margin-top: 0;
            color: ${theme.colors.campaigns.clearing.primaryTintEighty.hex};
        }

        @media ${theme.breakpoints.medium.media} {
            justify-content: space-around;
            column-gap: 1rem;
        }
    `}
`;

export const PostFooterWrapper = styled.div`
    ${({ theme }) => css`
        width: 100%;
        max-width: 90%;
        margin: 0 auto;
        transform: translateY(-2rem);
        margin-bottom: -2rem;

        @media ${theme.breakpoints.medium.media} {
            margin: 0;
            max-width: 100%;
            padding: 0 1rem;
        }

        @media ${theme.breakpoints.large.media} {
            margin: 0 auto;
            max-width: 90%;
            width: 60rem;
        }
    `}
`;

export const PostHeading = styled.h3`
    ${({ theme }) => css`
        ${campaignHeading()};
        color: inherit;
        margin: 0;
        text-align: center;
        font-size: 1.5rem;

        @media ${theme.breakpoints.large.media} {
            text-align: left;
            font-size: 1.75rem;
        }
    `}
`;

export const CopyContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: auto;
    justify-content: center;
    align-items: flex-start;
`;

export const Icon = styled(Calendar)`
    ${({ theme }) => css`
        display: none;

        @media ${theme.breakpoints.medium.media} {
            display: flex;
            width: 2.5rem;
            height: 2.5rem;
            margin-right: 1.25rem;
        }

        @media ${theme.breakpoints.large.media} {
            width: 2.5rem;
            height: 2.5rem;
        }
    `}
`;

export const DateTextWrapper = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: row;
        align-items: center;

        @media ${theme.breakpoints.medium.media} {
            flex-direction: column;
            align-items: flex-start;
        }
    `}
`;

export const DateText = styled.p`
    ${({ theme }) => css`
        margin: 0;
        font-weight: 700;
        margin-right: 0.5rem;

        @media ${theme.breakpoints.large.media} {
            margin: 0;
        }
    `}
`;

export const DateLabel = styled.span`
    ${({ theme }) => css`
        margin: 0;
        font-size: 1rem;

        @media ${theme.breakpoints.medium.media} {
            font-size: 1.125rem;
        }
    `}
`;
export const ClearingClosedLinkWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

export const Location = styled.span`
    ${({ theme }) => css`
        background-color: ${theme.colors.campaigns.clearing.primaryTintEighty.hex};
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        border-radius: 1rem;
        font-size: 1rem;
        padding: 0.125rem 0.5rem;
        text-align: center;
        display: inline-block;
        margin-top: 0.25rem;
        width: fit-content;

        @media ${theme.breakpoints.large.media} {
            padding: 0.125rem 1rem;
        }
    `}
`;

export const StyledLeftBottles = styled(NextImage)`
    ${({ theme }) => css`
        position: absolute;
        width: 38.4375rem;
        height: 38.4375rem;
        transform: scale(0.35);
        left: -19.25rem;
        top: -9.5rem;

        @media ${theme.breakpoints.medium.media} {
            transform: scale(0.5);
            left: -14rem;
            top: -5rem;
        }

        @media ${theme.breakpoints.large.media} {
            transform: scale(0.7);
            left: -12rem;
            top: 0;
        }

        @media only screen and (min-width: 93.75rem) {
            transform: scale(1);
            left: -4rem;
        }
    `}
`;

export const StyledRightBottles = styled(NextImage)`
    ${({ theme }) => css`
        position: absolute;
        width: 38.4375rem;
        height: 38.4375rem;
        transform: scale(0.4);
        right: -22.5rem;
        top: -8.5rem;

        @media ${theme.breakpoints.medium.media} {
            transform: scale(0.5);
            right: -13.5rem;
            top: -3rem;
        }

        @media ${theme.breakpoints.large.media} {
            transform: scale(0.7);
            right: -11.5rem;
            top: 0;
        }

        @media only screen and (min-width: 93.75rem) {
            transform: scale(1);
            right: -3.5rem;
        }
    `}
`;
